import type { DailyInspiration } from '../dto/DailyInspiration';
import { RoutineError, toRoutineError } from '../errors/RoutineError';
import type { LocalDateProvider } from '../ports/LocalDateProvider';
import type { PhilosophyRepository } from '../ports/PhilosophyRepository';
import { fromPhilosophyContentData } from '../../domain/philosophy/PhilosophyContent';
import { selectDailyPhilosophyIndex } from '../../domain/philosophy/SelectDailyPhilosophyEntry';
import { failure, success, type Result } from '../../domain/shared/Result';

export class GetTodayInspiration {
  public constructor(
    private readonly philosophy: PhilosophyRepository,
    private readonly dates: LocalDateProvider,
  ) {}

  public async execute(): Promise<Result<DailyInspiration, RoutineError>> {
    try {
      const entries = await this.philosophy.list();
      if (entries.length === 0) {
        return failure(new RoutineError('philosophy_corpus_empty', 'Aucune pensée n’est disponible.'));
      }
      /** Stable regardless of storage backend read order — see file header note in SeedPhilosophyCorpus. */
      const ordered = [...entries].sort(
        (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id),
      );
      const logicalDate = this.dates.today();
      const index = selectDailyPhilosophyIndex(logicalDate, ordered.length);
      const entry = ordered[index];
      const content = entry ? fromPhilosophyContentData(entry.content) : undefined;
      if (!entry || !content) {
        return failure(
          new RoutineError('philosophy_entry_invalid', 'Le contenu de cette pensée est invalide.'),
        );
      }
      return success({ id: entry.id, content, corpusIndex: index });
    } catch (error) {
      return failure(toRoutineError(error));
    }
  }
}
